import { SHEET_NAME as STAKEHOLDER_GROUP_SHEET } from "./stakeholderGroupSheet";

const HEADER_FILL = {
  type: "pattern",
  pattern: "solid",
  fgColor: { argb: "FFB9D1EA" },
};

const SUBHEADER_FILL = {
  type: "pattern",
  pattern: "solid",
  fgColor: { argb: "FFD9D9D9" },
};

const THIN_BLACK = {
  top: { style: "thin", color: { argb: "FF000000" } },
  left: { style: "thin", color: { argb: "FF000000" } },
  bottom: { style: "thin", color: { argb: "FF000000" } },
  right: { style: "thin", color: { argb: "FF000000" } },
};

const BODY_FONT = { name: "Calibri", size: 11 };
const LINK_FONT = {
  name: "Calibri",
  size: 11,
  color: { argb: "FF0563C1" },
  underline: true,
};

const PRINCIPLE_TITLE =
  "Businesses should respect the interests of and be responsive to all its stakeholders.";

const COL_D = 4;
const COL_E = 5;
const COL_END = COL_E;

function borderCell(cell) {
  cell.border = THIN_BLACK;
}

function setCell(ws, row, col, value, options = {}) {
  const cell = ws.getCell(row, col);
  if (options.link) {
    cell.value = { text: value || "Show Detail", hyperlink: options.link };
    cell.font = LINK_FONT;
  } else {
    cell.value = value;
    cell.font = options.bold ? { ...BODY_FONT, bold: true } : BODY_FONT;
  }
  borderCell(cell);
  cell.alignment = {
    horizontal: options.align ?? (options.link ? "center" : "left"),
    vertical: options.vertical || "middle",
    wrapText: true,
  };
  if (options.fill) cell.fill = options.fill;
}

function mergeRow(ws, row, startCol, endCol, value, options = {}) {
  if (startCol < endCol) ws.mergeCells(row, startCol, row, endCol);
  setCell(ws, row, startCol, value, options);
  for (let col = startCol + 1; col <= endCol; col++) {
    borderCell(ws.getCell(row, col));
  }
}

function safeText(value, fallback = "-") {
  if (value === null || value === undefined || value === "") return fallback;
  if (Array.isArray(value)) return value.length ? value.join(", ") : fallback;
  if (typeof value === "object") return fallback;
  return String(value);
}

function toYesNo(value) {
  if (value === true || value === "true" || value === "yes" || value === "Yes") return "Yes";
  if (value === false || value === "false" || value === "no" || value === "No") return "No";
  return "-";
}

function addSectionHeader(ws, row, title, fill = HEADER_FILL) {
  mergeRow(ws, row, COL_D, COL_END, title, { bold: true, align: "center", fill });
  return row + 1;
}

function addQuestionRow(ws, row, label, value, options = {}) {
  if (options.fullWidth || (value === "" && !options.valueCol)) {
    mergeRow(ws, row, COL_D, COL_END, label, { align: "left", vertical: "top", bold: options.bold });
    return row + 1;
  }
  const valueCol = options.valueCol || COL_E;
  const valueEnd = options.valueEnd || COL_END;
  mergeRow(ws, row, COL_D, valueCol - 1, label, { align: "left", vertical: "top", bold: options.bold });
  mergeRow(ws, row, valueCol, valueEnd, value ?? "", {
    align: options.valueAlign ?? (options.link ? "center" : "left"),
    vertical: "top",
    link: options.link,
  });
  return row + 1;
}

function addShowDetailRow(ws, row, label, text) {
  const hasText = text !== null && text !== undefined && String(text).trim() !== "";
  const value = hasText ? safeText(text) : "Show Detail";
  return addQuestionRow(ws, row, label, value, {
    valueCol: COL_E,
    valueEnd: COL_END,
    valueAlign: hasText ? "left" : "center",
  });
}

function addShowDetailLinkRow(ws, row, label, sheetName) {
  return addQuestionRow(ws, row, label, "Show Detail", {
    valueCol: COL_E,
    valueEnd: COL_END,
    valueAlign: "center",
    link: `#'${sheetName}'!D3`,
  });
}

export function buildPrinciple4Sheet(workbook, sectionC) {
  const ws = workbook.addWorksheet("PRINCIPLE 4", { views: [{ showGridLines: true }] });

  ws.getColumn("A").width = 2;
  ws.getColumn("B").width = 2;
  ws.getColumn("C").width = 2;
  ws.getColumn("D").width = 72;
  ws.getColumn("E").width = 16;

  const principle = sectionC?.principle4 || {};
  const essential = principle?.essentialIndicators || {};
  const leadership = principle?.leadershipIndicator || {};
  const integration = leadership?.stakeholderConsultationIntegration || {};

  let row = 3;
  row = addSectionHeader(ws, row, `PRINCIPLE 4: ${PRINCIPLE_TITLE}`);
  row = addSectionHeader(ws, row, "Essential Indicators", SUBHEADER_FILL);

  row = addShowDetailRow(
    ws,
    row,
    "1. Describe the processes for identifying key stakeholder groups of the entity.",
    essential?.stakeholderGroup
  );

  row = addShowDetailLinkRow(
    ws,
    row,
    "2. List stakeholder groups identified as key for your entity and the frequency of engagement with each stakeholder group.",
    STAKEHOLDER_GROUP_SHEET
  );

  row = addSectionHeader(ws, row, "Leadership Indicators", SUBHEADER_FILL);

  row = addShowDetailRow(
    ws,
    row,
    "1. Provide the processes for consultation between stakeholders and the Board on economic, environmental, and social topics or if consultation is delegated, how is feedback from such consultations provided to the Board.",
    leadership?.stakeholderConsultationProcesses
  );

  row = addQuestionRow(
    ws,
    row,
    "2. Whether stakeholder consultation is used to support the identification and management of environmental, and social topics (Yes / No).",
    toYesNo(integration?.action),
    { valueCol: COL_E, valueEnd: COL_END, valueAlign: "center" }
  );

  if (integration?.action) {
    row = addShowDetailRow(
      ws,
      row,
      "If so, provide details of instances as to how the inputs received from stakeholders on these topics were incorporated into policies and activities of the entity.",
      integration?.remark
    );
  }

  row = addShowDetailRow(
    ws,
    row,
    "3. Provide details of instances of engagement with, and actions taken to, address the concerns of vulnerable/ marginalized stakeholder groups.",
    leadership?.vulnerableStakeholderEngagement
  );

  return ws;
}
